import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Button,
    Table,
    DatePicker,
    Row,
    Col,
    Space,
    Typography,
    message,
    Modal,
    Card,
    Divider,
    Tag,
    Spin
} from 'antd';
import {
    ShoppingCartOutlined,
    UserOutlined,
    WalletOutlined,
    FileTextOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckOutlined,
    EditOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../axiosInstance';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { processDate, shopId } from '../../constants';
import './PostSales.css'; // Reuse the same CSS

const { Option } = Select;
const { Title, Text } = Typography;

const EditSales = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [customers, setCustomers] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [productsList, setProductsList] = useState([]);
    const [products, setProducts] = useState([{ id: '', quantity: '', selling_price: '', default: true, buying_price: '', inventory_id: null, discount: '' }]);
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(true);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedAccType, setSelectedAccType] = useState('cash');
    const [selectedProductDetails, setSelectedProductDetails] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [currentProductIndex, setCurrentProductIndex] = useState(null);
    const [scanBuffer, setScanBuffer] = useState("");
    const [originalSaleData, setOriginalSaleData] = useState(null);

    // Barcode scanner logic
    useEffect(() => {
        const handleKeyDown = (event) => {
            setScanBuffer((prev) => prev + event.key);

            if (event.key === "Enter" || event.key === "Tab" || event.key === "Enter" || event.key === "Shift" || event.key === "Control") {
                if (scanBuffer.trim() !== "") {
                    addProductByBarcode(scanBuffer.trim());
                    setScanBuffer("");
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [scanBuffer, productsList, products]);

    const addProductByBarcode = (barcode) => {
        const product = productsList.find((prod) => prod.barcode === barcode);
        if (product) {
            const existingProductIndex = products.findIndex((prod) => prod.id === product.id);

            if (existingProductIndex !== -1) {
                setProducts((prevProducts) => {
                    const updatedProducts = [...prevProducts];
                    updatedProducts[existingProductIndex].quantity += 1;
                    return updatedProducts;
                });
            } else {
                setProducts((prevProducts) => [
                    ...prevProducts,
                    {
                        id: product.id,
                        quantity: 1,
                        selling_price: product.selling_price || 0,
                        buying_price: product.buying_price || 0,
                        default: true,
                        inventory_id: null,
                        discount: 0,
                        barcode,
                    },
                ]);
            }
            message.success(`Product ${product.name} added via barcode scan.`);
        } else {
            message.error("Scanned product not found in inventory.");
        }
    };

    useEffect(() => {
        if (selectedType === 'receipt') {
            form.setFieldsValue({
                payment_date: moment(),
            });
        }
    }, [selectedType, form]);

    // Fetch all initial data including sale details
    useEffect(() => {
        const fetchAllData = async () => {
            setFetchingData(true);
            try {
                const [customersRes, accountsRes, productsRes, saleRes] = await Promise.all([
                    axiosInstance.get('/customers'),
                    axiosInstance.get(`/shops/${shopId}/account`),
                    axiosInstance.get('/products'),
                    axiosInstance.get(`/sales/${id}`)
                ]);

                setCustomers(customersRes.data.customers);
                setAccounts(accountsRes.data);
                setFilteredAccounts(accountsRes.data);
                setProductsList(productsRes.data.products);

                const saleData = saleRes.data;
                setOriginalSaleData(saleData);

                // Populate form with sale details
                form.setFieldsValue({
                    type: saleData.type,
                    customer: saleData.customer ? saleData.customer.id : null,
                    account: saleData.account ? saleData.account.id : null,
                    account_type: saleData.account_type || 'cash',
                    invoice_number: saleData.invoice_number,
                    receipt_number: saleData.receipt_number,
                    reverse_receipt_id: saleData.reverse_receipt_id,
                    due_date: saleData.due_date ? moment(saleData.due_date) : null,
                    payment_date: saleData.payment_date ? moment(saleData.payment_date) : null,
                    overall_discount: saleData.discount || 0,
                });

                // Populate products in the table
                const saleItems = saleData.items.map((item) => ({
                    id: item.product.id,
                    quantity: item.quantity,
                    selling_price: item.price,
                    buying_price: item.buying_price || 0,
                    default: !item.inventory_id,
                    inventory_id: item.inventory_id,
                    discount: item.discount || 0,
                }));

                setProducts(saleItems.length > 0 ? saleItems : [{ 
                    id: '', quantity: '', selling_price: '', default: true, 
                    buying_price: '', inventory_id: null, discount: '' 
                }]);

                setSelectedType(saleData.type);
                setSelectedAccType(saleData.account_type || 'cash');

                // Filter accounts based on the sale's account type
                if (saleData.account_type) {
                    filterAccountsByType(saleData.account_type);
                }

                message.success('Sale data loaded successfully');
            } catch (error) {
                console.error('Failed to fetch data:', error);
                message.error('Failed to load sale data. Please try again.');
            } finally {
                setFetchingData(false);
            }
        };

        fetchAllData();
    }, [id, form]);

    const handleProductChange = (index, field, value) => {
        setProducts((prevProducts) => {
            const updatedProducts = [...prevProducts];

            if (field === 'id') {
                updatedProducts[index][field] = value;
                const selectedProduct = productsList.find((prod) => prod.id === value);

                if (selectedProduct) {
                    updatedProducts[index]['selling_price'] = selectedProduct.selling_price || 0;
                    updatedProducts[index]['buying_price'] = selectedProduct.buying_price || 0;
                }
            } else {
                updatedProducts[index][field] = value;
            }

            return updatedProducts;
        });
    };

    const addProductRow = () => {
        setProducts([...products, { 
            id: '', quantity: '', selling_price: '', default: true, 
            buying_price: '', inventory_id: null, discount: '' 
        }]);
    };

    const removeProductRow = (index) => {
        const updatedProducts = [...products];
        updatedProducts.splice(index, 1);
        setProducts(updatedProducts);
    };

    const filterAccountsByType = (type) => {
        setFilteredAccounts(accounts.filter(account => account.type === type));
    };

    const handleValuesChange = (changedValues) => {
        if (changedValues.type) {
            setSelectedType(changedValues.type);
            // Don't reset all fields when editing, just update the type
        }

        if (changedValues.type === 'receipt') {
            filterAccountsByType(selectedAccType);
        }

        if (changedValues.account_type) {
            setSelectedAccType(changedValues.account_type);
            filterAccountsByType(changedValues.account_type);
            form.setFieldsValue({ account: undefined });
        }
    };

    const getNumericFieldValue = (form, fieldName) => {
        const value = form.getFieldValue(fieldName);
        return Number(value) || 0;
    };

    const confirmSubmit = (values) => {
        Modal.confirm({
            title: 'Confirm Sale Update',
            content: 'Are you sure you want to update this sale? This action will modify the existing sale record.',
            okText: 'Update Sale',
            cancelText: 'Cancel',
            okButtonProps: { type: 'primary' },
            onOk: () => handleSubmit(values),
        });
    };

    const handleSubmit = async (values) => {
        setLoading(true);

        const payload = {
            customer_id: values.customer || null,
            account_id: values.account || null,
            products: products.map((product) => ({
                id: product.id,
                quantity: Number(product.quantity),
                selling_price: product.selling_price,
                default: product.default,
                buying_price: product.buying_price,
                inventory_id: product.inventory_id || null,
                discount: product.discount || null
            })),
            invoice_number: values.invoice_number,
            receipt_number: values.receipt_number,
            type: values.type,
            due_date: processDate(values.due_date) || null,
            payment_date: processDate(values.payment_date) || null,
            reverse_receipt_id: values.reverse_receipt_id,
            total: totalPrice,
            account_type: values.account_type || selectedAccType,
            overall_discount: form.getFieldValue('overall_discount') || 0,
        };

        try {
            const validationResponse = await axiosInstance.post('/sales/validate-quantities', { 
                products: payload.products 
            });

            if (validationResponse.data.error) {
                message.error(validationResponse.data.error);
                return;
            }

            const updateResponse = await axiosInstance.put(`/sales/update/${id}`, payload);

            if (updateResponse.status === 200) {
                message.success('Sale updated successfully!');
                // navigate('/sales-list'); // Navigate back to sales list
            } else {
                message.error('Failed to update sale. Please try again.');
            }
        } catch (error) {
            if (error.response && error.response.data && error.response.data.error) {
                message.error(error.response.data.error);
            } else {
                message.error('An unexpected error occurred. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const openModal = async (index) => {
        try {
            const selectedProductId = products[index].id;
            const response = await axiosInstance.get(`/sales/sales-prices/${selectedProductId}`);
            if (response.data) {
                setSelectedProductDetails(response.data.details);
                setCurrentProductIndex(index);
                setModalVisible(true);
            }
        } catch (error) {
            console.error('Failed to fetch product details:', error);
            message.error('Failed to fetch product pricing details.');
        }
    };

    const handleSelectPrice = (record) => {
        setProducts((prevProducts) => {
            return prevProducts.map((product, index) =>
                index === currentProductIndex
                    ? { 
                        ...product, 
                        selling_price: record.selling_price, 
                        default: record.default, 
                        buying_price: record.buying_price, 
                        inventory_id: record.default ? null : record.id 
                    }
                    : product
            );
        });
        setModalVisible(false);
    };

    const productColumns = [
        {
            title: 'Product',
            dataIndex: 'id',
            render: (_, record, index) => (
                <Select
                    showSearch
                    value={record.id}
                    onChange={(value) => handleProductChange(index, 'id', value)}
                    style={{ width: '100%', minWidth: '200px' }}
                    placeholder="Search product..."
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        String(option?.children).toLowerCase().includes(input.toLowerCase())
                    }
                    suffixIcon={<ShoppingCartOutlined />}
                >
                    {productsList.map((prod) => (
                        <Option key={prod.id} value={prod.id}>
                            {prod.name} {prod.barcode && `(${prod.barcode})`}
                        </Option>
                    ))}
                </Select>
            ),
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            width: 120,
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.quantity}
                    onChange={(e) => {
                        const inputValue = e.target.value;
                        const numericValue = inputValue === '' ? '' : Number(inputValue);
                        const finalValue = isNaN(numericValue) ? 0 : Math.max(0, numericValue);
                        handleProductChange(index, 'quantity', finalValue);
                    }}
                    step="0.01"
                    placeholder="Qty"
                    min={0.01}
                    precision={2}
                />
            ),
        },
        {
            title: 'Unit Price',
            dataIndex: 'selling_price',
            width: 150,
            render: (_, record, index) => (
                <Space>
                    <Input
                        type="number"
                        value={record.selling_price}
                        onChange={(e) => handleProductChange(index, 'selling_price', parseFloat(e.target.value) || 0)}
                        min={0}
                        placeholder="Price"
                        disabled
                        style={{ fontWeight: 'bold', color: '#1890ff', width: '100px' }}
                    />
                    <Button
                        type="link"
                        onClick={() => openModal(index)}
                        icon={<FileTextOutlined />}
                        style={{ padding: '0 5px' }}
                    />
                </Space>
            ),
        },
        {
            title: 'Discount',
            dataIndex: 'discount',
            width: 120,
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.discount}
                    onChange={(e) => handleProductChange(index, 'discount', parseFloat(e.target.value) || 0)}
                    placeholder="Discount"
                    min={0}
                />
            ),
        },
        {
            title: 'Discount per Unit',
            dataIndex: 'discountPerUnit',
            render: (_, record, index) => (
                <Input
                    type="number"
                    value={record.discountPerUnit || 0}
                    onChange={(e) => {
                        const discountPerUnit = parseFloat(e.target.value) || 0;
                        const totalDiscount = record.quantity * discountPerUnit;

                        handleProductChange(index, 'discountPerUnit', discountPerUnit);
                        handleProductChange(index, 'discount', totalDiscount);
                    }}
                    placeholder="Enter Discount Per Unit"
                />
            ),
        },
        {
            title: 'Line Total',
            width: 120,
            render: (_, record) => (
                <Text strong>
                    {((record.quantity * record.selling_price) - (record.discount || 0)).toFixed(2)}
                </Text>
            ),
        },
        {
            title: 'Action',
            width: 80,
            render: (_, __, index) => (
                index > 0 && (
                    <Button
                        type="text"
                        danger
                        onClick={() => removeProductRow(index)}
                        icon={<DeleteOutlined />}
                        style={{ color: '#ff4d4f' }}
                    />
                )
            ),
        },
    ];

    const calculateTotal = () => {
        let totalQuantity = 0;
        let totalPrice = 0;
        const overallDiscount = form.getFieldValue('overall_discount') || 0;

        products.forEach(product => {
            const productDiscount = product.discount || 0;
            const productTotal = (product.quantity * product.selling_price) - productDiscount;
            totalQuantity += product.quantity;
            totalPrice += productTotal;
        });

        totalPrice -= overallDiscount;
        totalPrice = totalPrice < 0 ? 0 : totalPrice;

        return { totalQuantity, totalPrice };
    };

    const { totalQuantity, totalPrice } = calculateTotal();

    if (fetchingData) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <Spin size="large" />
                <Text style={{ marginLeft: '16px' }}>Loading sale data...</Text>
            </div>
        );
    }

    return (
        <div className="post-sales-container">
            <Card
                title={
                    <Space>
                        <EditOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                        <Title level={3} style={{ margin: 0 }}>
                            Edit Sale #{originalSaleData?.invoice_number || originalSaleData?.receipt_number || id}
                        </Title>
                        <Button 
                            type="text" 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate('/sales-list')}
                        >
                            Back to Sales
                        </Button>
                        <Button 
                            type="text" 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate(`/sales/view/${id}`)}
                        >
                            View
                        </Button>
                    </Space>
                }
                bordered={false}
                className="sales-card"
                extra={
                    <Tag color={selectedType === 'receipt' ? 'green' : selectedType === 'invoice' ? 'blue' : 'orange'}>
                        {selectedType?.toUpperCase()}
                    </Tag>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={handleValuesChange}
                    onFinish={confirmSubmit}
                >
                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                                label={<Text strong>Transaction Type</Text>}
                                name="type"
                                rules={[{ required: true, message: 'Please select transaction type' }]}
                            >
                                <Select
                                    placeholder="Select type"
                                    suffixIcon={<FileTextOutlined />}
                                >
                                    {/* <Option value="invoice">Invoice</Option>
                                    <Option value="receipt">Receipt</Option> */}
                                    <Option value="proforma">Proforma</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                                label={<Text strong>Customer</Text>}
                                name="customer"
                            >
                                <Select
                                    placeholder="Select customer"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                    suffixIcon={<UserOutlined />}
                                >
                                    {customers.map((customer) => (
                                        <Option key={customer.id} value={customer.id}>
                                            {customer.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        {selectedType === 'receipt' && (
                            <>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <Form.Item
                                        label={<Text strong>Payment Method</Text>}
                                        name="account_type"
                                        rules={[{ required: true, message: 'Please select payment method' }]}
                                    >
                                        <Select
                                            placeholder="Payment method"
                                            onChange={filterAccountsByType}
                                            suffixIcon={<WalletOutlined />}
                                        >
                                            <Option value="online">Online Payment</Option>
                                            <Option value="cash">Cash</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12} md={8} lg={6}>
                                    <Form.Item
                                        label={<Text strong>Account</Text>}
                                        name="account"
                                        rules={[{ required: false, message: 'Please select account' }]}
                                    >
                                        <Select
                                            placeholder="Select account"
                                            suffixIcon={<WalletOutlined />}
                                        >
                                            {filteredAccounts.map((account) => (
                                                <Option key={account.id} value={account.id}>
                                                    {account.account_name}
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                    </Row>

                    <Row gutter={[24, 16]}>
                        {selectedType === 'invoice' && (
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item
                                    label={<Text strong>Invoice Number</Text>}
                                    name="invoice_number"
                                >
                                    <Input
                                        placeholder="INV-0001"
                                        prefix={<FileTextOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {selectedType === 'receipt' && (
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item
                                    label={<Text strong>Receipt Number</Text>}
                                    name="receipt_number"
                                >
                                    <Input
                                        placeholder="RCP-0001"
                                        prefix={<FileTextOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {(selectedType === 'invoice' || selectedType === 'proforma') && (
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item
                                    label={<Text strong>Due Date</Text>}
                                    name="due_date"
                                    rules={[{ required: true, message: 'Please select due date' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        placeholder="Select due date"
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {selectedType === 'receipt' && (
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item
                                    label={<Text strong>Payment Date</Text>}
                                    name="payment_date"
                                    rules={[{ required: true, message: 'Please select payment date' }]}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        placeholder="Select payment date"
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Form.Item
                                label={<Text strong>Reverse Receipt ID</Text>}
                                name="reverse_receipt_id"
                            >
                                <Input
                                    placeholder="Enter Reverse Receipt ID"
                                    prefix={<FileTextOutlined />}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left" style={{ color: '#1890ff' }}>
                        <ShoppingCartOutlined /> Products
                    </Divider>

                    <div className="product-table-container">
                        <Table
                            rowKey={(record, index) => `product-${index}-${record.id}`}
                            columns={productColumns}
                            dataSource={products}
                            pagination={false}
                            bordered
                            size="middle"
                            scroll={{ x: 'max-content' }}
                            className="product-table"
                            footer={() => (
                                <div className="table-footer">
                                    <div className="footer-totals">
                                        <div className="total-item">
                                            <Text strong>Total Items:</Text>
                                            <Text strong>{totalQuantity}</Text>
                                        </div>
                                        <div className="total-item">
                                            <Text strong>Subtotal:</Text>
                                            <Text strong>{totalPrice.toFixed(2)}</Text>
                                        </div>
                                    </div>
                                    <div className="footer-discount">
                                        <Form.Item
                                            label={<Text type="secondary">Overall Discount</Text>}
                                            name="overall_discount"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Input
                                                type="number"
                                                placeholder="Enter discount amount"
                                                style={{ width: '200px' }}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className="footer-grand-total">
                                        <Text strong>Grand Total:</Text>
                                        <Text strong style={{ fontSize: '1.2em', color: '#1890ff' }}>
                                            {
                                                (totalPrice).toFixed(2)
                                            }
                                        </Text>
                                    </div>
                                </div>
                            )}
                        />
                    </div>

                    <div className="actions-container">
                        <Button
                            type="dashed"
                            onClick={addProductRow}
                            icon={<PlusOutlined />}
                            className="add-product-btn"
                        >
                            Add Product
                        </Button>

                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            icon={<CheckOutlined />}
                            className="submit-btn"
                            size="large"
                        >
                            Update Sale
                        </Button>

                        <Button
                            type="default"
                            onClick={() => navigate('/sales')}
                            className="cancel-btn"
                            size="large"
                        >
                            Cancel
                        </Button>
                    </div>
                </Form>
            </Card>

            <Modal
                title={<><ShoppingCartOutlined /> Product Pricing History</>}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
                centered
            >
                <Table
                    dataSource={selectedProductDetails}
                    rowKey="id"
                    columns={[
                        {
                            title: 'Buying Price',
                            dataIndex: 'buying_price',
                            render: (text) => <Text>{parseFloat(text).toFixed(2)}</Text>
                        },
                        {
                            title: 'Selling Price',
                            dataIndex: 'selling_price',
                            render: (text) => <Text strong>{parseFloat(text).toFixed(2)}</Text>
                        },
                        {
                            title: 'Quantity',
                            dataIndex: 'quantity',
                            render: (text) => <Tag color="blue">{text}</Tag>
                        },
                        {
                            title: 'Expire Date',
                            dataIndex: 'expire_date',
                            render: (text) => <Text type="secondary">{text || 'N/A'}</Text>
                        },
                        {
                            title: 'Action',
                            width: 100,
                            render: (_, record) => (
                                <Button
                                    type="primary"
                                    onClick={() => handleSelectPrice(record)}
                                    size="small"
                                >
                                    Select
                                </Button>
                            ),
                        },
                    ]}
                    pagination={false}
                    summary={() => (
                        <Table.Summary>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={3}>
                                    <Text strong>Total Available:</Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <Text strong>
                                        {selectedProductDetails.reduce((total, record) => total + record.quantity, 0)}
                                    </Text>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            </Modal>
        </div>
    );
};

export default EditSales;